import { StrictMode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import * as ReactDOM from 'react-dom/client';
import App from './app/app.js';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient, trpcClient } from './trpcClient.js';
import './styles.css';
import '@shelflix/ui-web/styles.css';
import './styles/button-variants.css';
import * as Sentry from '@sentry/react';
import { applyThemeColor, applyFavicon } from './utils/utils.js';
import { useConfigStore } from './store/configStore.js';
import { preloadImages } from './utils/imagePreloadCache.js';
import shelflixFavicon from './assets/shelflix-favicon.ico';
import { AppErrorBoundary } from './components/AppErrorBoundary.js';
import { validateStorageHealth, clearAuthData } from './utils/auth.js';

/**
 * Extract tenant slug from the current domain for multi-domain architecture.
 * Each tenant has its own subdomain (e.g., heineken.shelflix.com.br)
 */
function getLoginTenantSlug(): string {
  const hostname = window.location.hostname;

  // Multi-domain architecture: detect tenant from domain
  if (hostname.endsWith('.shelflix.com.br')) {
    const subdomain = hostname.split('.')[0];
    // Map special subdomains
    if (subdomain === 'app') return 'heineken'; // Legacy redirect
    if (subdomain === 'www' || subdomain === 'hml') return 'default';
    // Return subdomain as tenant slug (e.g., heineken, gallo, trescoracoes)
    return subdomain;
  }

  // Local development or custom domain: fallback to "default" tenant
  return 'default';
}

function isWwwSubdomain(): boolean {
  const hostname = window.location.hostname;
  if (hostname === 'shelflix.com.br') return true;
  return hostname.startsWith('www.') && hostname.endsWith('.shelflix.com.br');
}

// Validate and initialize Sentry only with proper DSN format
const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;
if (sentryDsn) {
  // Sentry DSN format: https://<public_key>@<host>/<project_id>
  // Invalid format: https://<host>/<project_id> (missing public key)
  const isValidDsn = /^https:\/\/[a-f0-9]+@.+\/\d+$/.test(sentryDsn);

  if (isValidDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 1.0,
      environment: import.meta.env.MODE || 'development',
    });
  }
}

const rootElement = document.getElementById('root') as HTMLElement;
const root = ReactDOM.createRoot(rootElement);

// Wait for critical fonts to load before showing content
async function waitForFonts(): Promise<void> {
  // Check if Font Loading API is supported
  if ('fonts' in document) {
    try {
      // Wait for fonts to be ready (with timeout to prevent blocking)
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, 2000)), // 2s timeout
      ]);

      // Check specifically for Poppins font
      const poppinsLoaded = document.fonts.check('16px Poppins');
      if (!poppinsLoaded) {
        // Give a bit more time for Poppins to load
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch {
      // Font loading failed, continue anyway
    }
  }

  // Mark fonts as loaded to show content
  rootElement.classList.remove('fonts-loading');
  rootElement.classList.add('fonts-loaded');
}

async function initApp() {
  // Legacy domain redirect: app.shelflix.com.br → heineken.shelflix.com.br
  // Users were accustomed to app.shelflix.com.br, which was originally Heineken's deployment
  const hostname = window.location.hostname;
  if (hostname === 'app.shelflix.com.br') {
    const newUrl = window.location.href.replace('app.shelflix.com.br', 'heineken.shelflix.com.br');
    window.location.replace(newUrl);
    return; // Stop execution, browser will redirect
  }

  // Validate localStorage health before anything else
  // This catches corrupted auth tokens early
  validateStorageHealth();

  // Wait for fonts to load first to prevent FOUT
  await waitForFonts();

  // Multi-domain strategy: tenant is always determined by the domain
  const tenantSlugToLoad = getLoginTenantSlug();

  try {
    const configResponse = await trpcClient.applicationConfig.get.query({
      tenantSlug: tenantSlugToLoad,
    });
    const config = configResponse.config;

    // Preload logos using the global cache - this marks them as preloaded
    // so components won't show loading skeletons for already-cached images
    if (config?.logoUrl || config?.logoDarkUrl) {
      // Use the centralized preload cache which tracks loaded images globally
      await Promise.race([
        preloadImages([config?.logoUrl, config?.logoDarkUrl]),
        new Promise((resolve) => setTimeout(resolve, 1000)), // 1s timeout for initial load
      ]);
    }

    if (config?.themeColor) {
      const savedDarkMode = localStorage.getItem('darkMode');
      const isDarkMode = savedDarkMode ? JSON.parse(savedDarkMode) : true;
      applyThemeColor(config.themeColor, isDarkMode);
    }

    if (isWwwSubdomain()) {
      applyFavicon(shelflixFavicon);
    } else if (config?.faviconUrl) {
      applyFavicon(config.faviconUrl);
    } else {
      // Use default Shelflix favicon when tenant doesn't have one
      applyFavicon(shelflixFavicon);
    }

    // Update page title with tenant name
    if (isWwwSubdomain()) {
      document.title = 'Shelflix';
    } else if (config?.name) {
      document.title = config.name;
    }

    const { setConfig } = useConfigStore.getState();
    setConfig(config);
  } catch (error) {
    // Log error to Sentry
    Sentry.captureException(error, {
      extra: {
        context: 'initApp - applicationConfig.get failed',
        tenantSlug: tenantSlugToLoad,
      },
    });

    // If config loading fails due to auth issues, clear auth data
    // and let the app render - ProtectedRoute will redirect to login
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (
      errorMessage.includes('jwt') ||
      errorMessage.includes('token') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('UNAUTHORIZED')
    ) {
      clearAuthData();
    }

    // Apply default favicon so the app doesn't look completely broken
    applyFavicon(shelflixFavicon);

    // Continue to render the app - it will still work, just without custom config
  }

  // Always render the app, even if config loading failed
  renderApp();
}

function renderApp() {
  root.render(
    <StrictMode>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AppErrorBoundary>
            <App />
          </AppErrorBoundary>
        </QueryClientProvider>
      </BrowserRouter>
    </StrictMode>
  );
}

// Handle any uncaught errors during initialization
try {
  initApp();
} catch (error) {
  // Last resort: if initApp itself throws synchronously, still try to render something
  Sentry.captureException(error, {
    extra: { context: 'initApp - synchronous error' },
  });

  clearAuthData();
  renderApp();
}
